import {ErrorInfo} from 'react'
import * as Sentry from '@sentry/react'
import {createBrowserHistory} from 'history'
import {UserInfo} from 'interfaces'

const history = createBrowserHistory()

/**
 * Filters out Sentry events for DLP Dopamine entitlement errors (ERR_NOT_ENTITLED).
 * @param hint Sentry hint object containing the original exception
 * @returns true if the event should be ignored, false otherwise
 */
export const shouldIgnoreDlpDopamineEntitlement = (
  hint: Sentry.EventHint,
): boolean => {
  try {
    // Strict path pattern for the Dopamine endpoint associated with a specific file hash violation.
    const TARGET_PATTERN = /\/fe-violations\/file-hash\/[a-f0-9]+\/dopamine$/
    const ENTITLEMENT_CODE = 'ERR_NOT_ENTITLED'
    const originalException: any = hint?.originalException
    if (!originalException) return false

    // Gather possible URL locations from axios error structure.
    const rawUrl =
      originalException?.response?.config?.url ||
      originalException?.response?.request?.responseURL ||
      ''
    if (!rawUrl) return false

    if (!TARGET_PATTERN.test(rawUrl)) return false

    // Currently we only inspect response.data.code for the entitlement indicator.
    return originalException?.response?.data?.code === ENTITLEMENT_CODE
  } catch {
    // On any unexpected shape or runtime error, default to not ignoring.
    return false
  }
}

const init = () => {
  // Sentry initialization with beforeSend filter using the exported helper.

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV,
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({history}),
      Sentry.replayIntegration({
        maskAllText: false,
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    profilesSampleRate: 1.0, // Capture 100% of the profiles
    replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    release: process.env.REACT_APP_SENTRY_RELEASE,
    sendDefaultPii: true,
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    beforeSend: (event, hint) => {
      const isDlpDopamineEventIgnored = shouldIgnoreDlpDopamineEntitlement(hint)
      // Flag to indicate whether the event should be ignored or not
      const eventShouldBeIgnored = isDlpDopamineEventIgnored
      return eventShouldBeIgnored ? null : event
    },
  })
}

const SetUserLogScope = (user: Partial<UserInfo>) => {
  Sentry.getCurrentScope()?.setUser({
    id: user.emailId,
  })
}

const LogUncaughtError = (error: Error, errorInfo: ErrorInfo) => {
  Sentry.withScope(scope => {
    scope.setExtra('Uncaught Error', errorInfo)
    Sentry.captureException(error)
  })
}

const LogError = (error: Error) => {
  Sentry.withScope(scope => {
    scope.setExtra('error', error)
    Sentry.captureException(error)
  })
}

export default {init, SetUserLogScope, LogError, LogUncaughtError}
